import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import {
  ChartIcons,
  HighlightsIcons,
  InfoIcons,
  NotebookIcons,
  QuestionIcons,
} from '../../../../../../../icons/icons';
import { Drawer, NameEmailAvatar } from '../../../../../../../ui';
import { PencilIcon } from '../../../../../../../ui/icons/icons';
import { getInterviewRound } from '../../../../../../../utils/api_service/SmartInterview';
import ScoreStatus from '../Components/ScoreStatus';
import { IInterviewResult } from '../Hooks';
import { Highlights } from './Tabs/Highlights';
import { Notes } from './Tabs/Notes';
import { QA } from './Tabs/QnA';
import { Transcripts } from './Tabs/Transcripts ';

interface IUserDetails {
  isOpen: boolean;
  onCloseDrawer: () => void;
  result: IInterviewResult | null;
}

export default function UserDetails({
  isOpen,
  onCloseDrawer,
  result,
}: IUserDetails) {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const [activeTab, setActiveTab] = useState('overallScore');
  const [activeContentTab, setActiveContentTab] = useState('highlights');
  const [panelistsMap, setPanelistsMap] = useState<Record<string, any>>({});

  // Fetch panelists info when drawer opens
  useEffect(() => {
    if (!isOpen || !result?.interviewRoundId) return;

    const fetchPanelistsInfo = async () => {
      try {
        const response = await getInterviewRound(result.interviewRoundId);
        if (!response.ok) throw new Error(`Status ${response.status}`);
        const round = await response.json();

        const panelists: Record<string, any> = {};
        const panelistsArray = round?.panelists || [];
        panelistsArray.forEach((p: any) => {
          if (p?.user?.id) {
            panelists[p.id] = {
              firstName: p.user.firstName,
              lastName: p.user.lastName,
              email: p.user.email,
              avatar: p.user.profilePic || '',
            };
          }
        });

        setPanelistsMap(panelists);
      } catch (err) {
        setPanelistsMap({});
      }
    };

    fetchPanelistsInfo();
  }, [isOpen, result?.interviewRoundId]);

  // Prepare scores data
  const noJustification = t(SI.results.userDetails.noJustification);
  const scores = result
    ? [
      {
        key: 'overallScore',
        label: t(SI.results.userDetails.scores.overall),
        value: `${result.aiOverallScore?.score ?? 0}%`,
        analysis: t(SI.results.userDetails.aiAnalysis),
      },
      {
        key: 'technicalSkills',
        label: t(SI.results.userDetails.scores.technical),
        value: `${result.technicalSkills?.score ?? 0}%`,
        analysis: result.technicalSkills?.justification || noJustification,
      },
      {
        key: 'roleExperience',
        label: t(SI.results.userDetails.scores.roleExperience),
        value: `${result.roleExperience?.score ?? 0}%`,
        analysis: result.roleExperience?.justification || noJustification,
      },
      {
        key: 'industrySkills',
        label: t(SI.results.userDetails.scores.industrySkills),
        value: `${result.industrySkills?.score ?? 0}%`,
        analysis: result.industrySkills?.justification || noJustification,
      },
      {
        key: 'productKnowledge',
        label: t(SI.results.userDetails.scores.productKnowledge),
        value: `${result.productKnowledge?.score ?? 0}%`,
        analysis: result.productKnowledge?.justification || noJustification,
      },
      {
        key: 'communication',
        label: t(SI.results.userDetails.scores.communication),
        value: `${result.communication?.score ?? 0}%`,
        analysis: result.communication?.justification || noJustification,
      },
      {
        key: 'problemSolving',
        label: t(SI.results.userDetails.scores.problemSolving),
        value: `${result.problemSolving?.score ?? 0}%`,
        analysis: result.problemSolving?.justification || noJustification,
      },
      {
        key: 'leadership',
        label: t(SI.results.userDetails.scores.leadership),
        value: `${result.leadership?.score ?? 0}%`,
        analysis: result.leadership?.justification || noJustification,
      },
      {
        key: 'teamCollaboration',
        label: t(SI.results.userDetails.scores.teamCollaboration),
        value: `${result.teamCollaboration?.score ?? 0}%`,
        analysis: result.teamCollaboration?.justification || noJustification,
      },
    ]
    : [];

  // Get the selected score details based on activeTab
  const activeScore = scores.find((score) => score.key === activeTab);

  return (
    <Drawer
        title={`${result?.user?.firstName || ''} ${result?.user?.lastName || ''}`}
        isOpen={isOpen}
        onClose={onCloseDrawer}
        width='w-full md:w-[900px]'
    >
      <div className='flex flex-col lg:flex-row gap-6 p-6'>
        { /* Left Side: Score Type Tabs */ }
        <div className='lg:w-1/3'>
          <div className='sticky top-0'>
            <h3 className='text-xs font-bold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-2'>
              <svg
                  className='w-4 h-4 text-green-500'
                  fill='currentColor'
                  viewBox='0 0 20 20'
              >
                <path d='M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z' />
              </svg>
              { t(SI.results.userDetails.evaluationMetrics) }
            </h3>
            <div className='space-y-2'>
              { scores.map((score, idx) => {
                const scoreValue = Number(
                  score.value.toString().replace('%', ''),
                );
                const isActive = activeTab === score.label;

                // Color coding based on score
                let scoreColor = 'text-gray-500';
                let activeGradient = 'from-gray-600 to-gray-700';
                let activeShadow = 'shadow-gray-500/30';
                let hoverBorder = 'hover:border-gray-300';

                if (scoreValue >= 80) {
                  scoreColor = 'text-green-600';
                  activeGradient = 'from-green-600 to-emerald-600';
                  activeShadow = 'shadow-green-500/30';
                  hoverBorder = 'hover:border-green-300';
                } else if (scoreValue >= 60) {
                  scoreColor = 'text-blue-600';
                  activeGradient = 'from-blue-600 to-indigo-600';
                  activeShadow = 'shadow-blue-500/30';
                  hoverBorder = 'hover:border-blue-300';
                } else if (scoreValue >= 40) {
                  scoreColor = 'text-orange-600';
                  activeGradient = 'from-orange-600 to-amber-600';
                  activeShadow = 'shadow-orange-500/30';
                  hoverBorder = 'hover:border-orange-300';
                } else {
                  scoreColor = 'text-red-600';
                  activeGradient = 'from-red-600 to-rose-600';
                  activeShadow = 'shadow-red-500/30';
                  hoverBorder = 'hover:border-red-300';
                }

                return (
                  <button
                      key={idx}
                      className={`group w-full py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? `bg-gradient-to-r ${activeGradient} text-white shadow-lg ${activeShadow} scale-105`
                          : `bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 ${hoverBorder} hover:shadow-md`
                      }`}
                      onClick={() => setActiveTab(score.key)}
                  >
                    <div className='flex items-center justify-between'>
                      <span className='text-left'>{ score.label }</span>
                      <span
                          className={`font-bold ${
                            isActive ? 'text-white' : scoreColor
                          }`}
                      >
                        { score.value }
                      </span>
                    </div>
                  </button>
                );
              }) }
            </div>
          </div>
        </div>

        { /* Right Side: Score Details */ }
        <div className='lg:w-2/3'>
          { activeScore && (
            <div className='space-y-6'>
              { /* User Information Card */ }
              <div className='bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100'>
                <NameEmailAvatar
                    avatar={''} // You may replace with a real avatar URL
                    avatarHeight='80px'
                    avatarWidth='80px'
                    email={result?.user?.email || ''}
                    name={`${result?.user?.firstName || ''} ${
                      result?.user?.lastName || ''
                    }`}
                />
              </div>

              { /* Score Details Card */ }
              <div className='bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200'>
                <div className='p-6'>
                  <div className='flex items-start justify-between mb-4'>
                    <div className='flex items-center gap-3'>
                      <div className='w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg'>
                        <ChartIcons className='text-white' />
                      </div>
                      <div>
                        <h3 className='text-lg font-bold text-gray-900'>
                          { activeScore.label }
                        </h3>
                        <p className='text-sm text-gray-500'>
                          { t(SI.results.userDetails.aiAnalysis) }
                        </p>
                      </div>
                    </div>
                    <div className='flex-shrink-0'>
                      <ScoreStatus
                          score={Number(
                            activeScore.value.toString().replace('%', ''),
                          )}
                      />
                    </div>
                  </div>

                  { /* Analysis Section */ }
                  <div className='mt-6 p-4 bg-gradient-to-br from-gray-50 to-green-50 rounded-xl border border-gray-100'>
                    <div className='flex items-start gap-3'>
                      <div className='flex-shrink-0 mt-1'>
                        <InfoIcons className='text-green-600' />
                      </div>
                      <div className='flex-1'>
                        <h4 className='text-sm font-semibold text-gray-700 mb-2'>
                          { t(SI.results.userDetails.assessmentJustification) }
                        </h4>
                        <p className='text-sm text-gray-600 leading-relaxed'>
                          { activeScore.analysis }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) }
        </div>
      </div>

      { /* Content Tabs Section */ }
      <div className='mt-8 border-t border-gray-200 bg-gradient-to-br from-gray-50 to-white'>
        { /* Tab Navigation */ }
        <div className='sticky top-0 bg-white border-b border-gray-200 z-10 shadow-sm'>
          <div className='flex items-center px-6 overflow-x-auto'>
            <button
                onClick={() => setActiveContentTab('highlights')}
                className={`relative py-4 px-6 font-medium transition-all duration-200 whitespace-nowrap ${
                  activeContentTab === 'highlights'
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className='flex items-center gap-2'>
                <HighlightsIcons className='w-5 h-5' />
                { t(SI.results.userDetails.tabs.highlights) }
              </div>
              { activeContentTab === 'highlights' && (
                <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500'></div>
              ) }
            </button>
            <button
                onClick={() => setActiveContentTab('transcripts')}
                className={`relative py-4 px-6 font-medium transition-all duration-200 whitespace-nowrap ${
                  activeContentTab === 'transcripts'
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className='flex items-center gap-2'>
                <NotebookIcons className='w-5 h-5' />
                { t(SI.results.userDetails.tabs.transcripts) }
              </div>
              { activeContentTab === 'transcripts' && (
                <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500'></div>
              ) }
            </button>
            <button
                onClick={() => setActiveContentTab('q&a')}
                className={`relative py-4 px-6 font-medium transition-all duration-200 whitespace-nowrap ${
                  activeContentTab === 'q&a'
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className='flex items-center gap-2'><QuestionIcons className='w-5 h-5' /> { t(SI.results.userDetails.tabs.qa) }</div>
              { activeContentTab === 'q&a' && (
                <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500'></div>
              ) }
            </button>
            <button
                onClick={() => setActiveContentTab('notes')}
                className={`relative py-4 px-6 font-medium transition-all duration-200 whitespace-nowrap ${
                  activeContentTab === 'notes'
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <div className='flex items-center gap-2'>
                <PencilIcon className='w-5 h-5' />
                { t(SI.results.userDetails.tabs.panelistNotes) }
              </div>
              { activeContentTab === 'notes' && (
                <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-500'></div>
              ) }
            </button>
          </div>
        </div>

        { /* Tab Content */ }
        <div className='p-6'>
          { activeContentTab === 'highlights' && (
            <Highlights data={result?.summary || []} />
          ) }
          { activeContentTab === 'transcripts' && (
            <Transcripts data={result?.transcript || []} />
          ) }
          { activeContentTab === 'q&a' && (
            <QA data={result?.questionAndAnswer || []} />
          ) }
          { activeContentTab === 'notes' && (
            <Notes
                data={result?.panelistNotes || []}
                panelistsMap={panelistsMap}
            />
          ) }
        </div>
      </div>
    </Drawer>
  );
}
